// CSV Import/Export utilities for project data

export interface CSVProjectRow {
  job_location: string;
  job_address: string;
  project_details: string;
  normal_rate: string;
  overtime_rate: string;
  saturday_rate: string;
  saturday_contract_ot_rate: string;
  status: string;
  builder_name: string;
  builder_address: string;
  builder_poc_name: string;
  builder_email: string;
  category: string;
  subcategory: string;
  amount: string;
}

// Template headers
export const CSV_HEADERS = [
  "job_location",
  "job_address",
  "project_details",
  "normal_rate",
  "overtime_rate",
  "saturday_rate",
  "saturday_contract_ot_rate",
  "status",
  "builder_name",
  "builder_address",
  "builder_poc_name",
  "builder_email",
  "category",
  "subcategory",
  "amount",
];

// Sample template data with example rows (includes Fixed Amount example)
export const CSV_TEMPLATE_DATA = `job_location,job_address,project_details,normal_rate,overtime_rate,saturday_rate,saturday_contract_ot_rate,status,builder_name,builder_address,builder_poc_name,builder_email,category,subcategory,amount
MyProject,123 Main St Brisbane,Project description,85,127.50,400,,active,ABC Construction,456 Builder St,John Smith,john@abc.com,COLUMNS,Ground,15000
MyProject,123 Main St Brisbane,Project description,85,127.50,400,,active,ABC Construction,456 Builder St,John Smith,john@abc.com,COLUMNS,Level 1,12000
MyProject,123 Main St Brisbane,Project description,85,127.50,400,,active,ABC Construction,456 Builder St,John Smith,john@abc.com,WALLS,Ground,8000
MyProject,123 Main St Brisbane,Project description,85,127.50,400,,active,ABC Construction,456 Builder St,John Smith,john@abc.com,WALLS,Level 1,9500
MyProject,123 Main St Brisbane,Project description,85,127.50,400,,active,ABC Construction,456 Builder St,John Smith,john@abc.com,MISC_EXPENSES,,5000`;

// Download CSV template
export function downloadCSVTemplate(
  availableProjects: { id: string; name: string }[] = [],
) {
  let content = "";

  // Add available projects as comments if provided
  if (availableProjects.length > 0) {
    content += "# AVAILABLE PROJECTS FROM DASHPIVOT\n";
    content +=
      "# Copy one of these EXACTLY into the 'Job Location' column to ensure proper mapping:\n";
    availableProjects.forEach((p) => {
      content += `# - ${p.name}\n`;
    });
    content += "#\n";
  } else {
    content +=
      "# TIP: Enter the exact project name from Dashpivot in 'Job Location'\n";
    content += "#\n";
  }

  content += CSV_TEMPLATE_DATA;

  const blob = new Blob([content], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "project_import_template.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Parse CSV string to rows
export function parseCSV(csvText: string): CSVProjectRow[] {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) {
    throw new Error("CSV must have a header row and at least one data row");
  }

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

  // Validate headers - subcategory is optional (can be blank for Fixed Amount categories)
  const requiredHeaders = ["job_location", "category", "amount"];
  for (const required of requiredHeaders) {
    if (!headers.includes(required)) {
      throw new Error(`Missing required column: ${required}`);
    }
  }

  const rows: CSVProjectRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Handle quoted values with commas
    const values = parseCSVLine(line);

    if (values.length !== headers.length) {
      throw new Error(
        `Row ${i + 1} has ${values.length} values but expected ${
          headers.length
        }`,
      );
    }

    const row: CSVProjectRow = {
      job_location: "",
      job_address: "",
      project_details: "",
      normal_rate: "85",
      overtime_rate: "127.50",
      saturday_rate: "400",
      saturday_contract_ot_rate: "",
      status: "active",
      builder_name: "",
      builder_address: "",
      builder_poc_name: "",
      builder_email: "",
      category: "",
      subcategory: "",
      amount: "0",
    };

    headers.forEach((header, index) => {
      const value = values[index]?.trim() || "";
      switch (header) {
        case "job_location":
          row.job_location = value;
          break;
        case "job_address":
          row.job_address = value;
          break;
        case "project_details":
          row.project_details = value;
          break;
        case "normal_rate":
          row.normal_rate = value || "85";
          break;
        case "overtime_rate":
          row.overtime_rate = value || "127.50";
          break;
        case "saturday_rate":
          row.saturday_rate = value || "400";
          break;
        case "saturday_contract_ot_rate":
          row.saturday_contract_ot_rate = value || "";
          break;
        case "status":
          row.status = value || "active";
          break;
        case "builder_name":
          row.builder_name = value;
          break;
        case "builder_address":
          row.builder_address = value;
          break;
        case "builder_poc_name":
          row.builder_poc_name = value;
          break;
        case "builder_email":
          row.builder_email = value;
          break;
        case "category":
          row.category = value.toUpperCase();
          break;
        case "subcategory":
          row.subcategory = value; // Can be blank for Fixed Amount categories
          break;
        case "amount":
          row.amount = value;
          break;
      }
    });

    // Only require job_location and category - subcategory can be blank
    if (row.job_location && row.category) {
      rows.push(row);
    }
  }

  if (rows.length === 0) {
    throw new Error("No valid data rows found in CSV");
  }

  return rows;
}

// Parse a single CSV line handling quoted values
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
}

// Transform CSV rows into project data structure
export interface ParsedProjectData {
  job_location: string;
  job_address: string;
  project_details: string;
  normal_rate: number;
  overtime_rate: number;
  saturday_rate: number;
  saturday_contract_ot_rate: number | null;
  status: string;
  builder_name: string;
  builder_address: string;
  builder_poc_name: string;
  builder_email: string;
  categories: {
    name: string;
    isFixedAmount: boolean; // true if blank subcategory
    fixedAmount: number; // only used if isFixedAmount is true
    subcategories: {
      name: string;
      amount: number;
    }[];
  }[];
  discounts: {
    description: string;
    amount: number;
  }[];
}

export function transformCSVToProjectData(
  rows: CSVProjectRow[],
): ParsedProjectData {
  if (rows.length === 0) {
    throw new Error("No data rows to transform");
  }

  // Get project details from first row
  const firstRow = rows[0];

  // Group by category, tracking if it's fixed amount or has subcategories
  const categoryData = new Map<
    string,
    {
      isFixedAmount: boolean;
      fixedAmount: number;
      subcategories: Map<string, number>;
    }
  >();

  const discounts: { description: string; amount: number }[] = [];

  for (const row of rows) {
    if (row.category.toUpperCase() === "DISCOUNT") {
      discounts.push({
        description: row.subcategory || "Discount",
        amount: parseFloat(row.amount) || 0,
      });
      continue;
    }

    if (!categoryData.has(row.category)) {
      categoryData.set(row.category, {
        isFixedAmount: false,
        fixedAmount: 0,
        subcategories: new Map(),
      });
    }

    const catData = categoryData.get(row.category)!;

    if (!row.subcategory || row.subcategory.trim() === "") {
      // Blank subcategory = Fixed Amount category
      catData.isFixedAmount = true;
      catData.fixedAmount = parseFloat(row.amount) || 0;
    } else {
      // Has subcategory = normal category with subcategories
      catData.subcategories.set(row.subcategory, parseFloat(row.amount) || 0);
    }
  }

  // Convert to array structure
  const categories = Array.from(categoryData.entries()).map(
    ([categoryName, catData]) => ({
      name: categoryName,
      isFixedAmount: catData.isFixedAmount && catData.subcategories.size === 0,
      fixedAmount: catData.fixedAmount,
      subcategories: Array.from(catData.subcategories.entries()).map(
        ([subName, amount]) => ({
          name: subName,
          amount,
        }),
      ),
    }),
  );

  return {
    job_location: firstRow.job_location,
    job_address: firstRow.job_address || "",
    project_details: firstRow.project_details || "",
    normal_rate: parseFloat(firstRow.normal_rate) || 85,
    overtime_rate: parseFloat(firstRow.overtime_rate) || 127.5,
    saturday_rate: parseFloat(firstRow.saturday_rate) || 400,
    saturday_contract_ot_rate: firstRow.saturday_contract_ot_rate
      ? parseFloat(firstRow.saturday_contract_ot_rate)
      : null,
    status: firstRow.status || "active",
    builder_name: firstRow.builder_name || "",
    builder_address: firstRow.builder_address || "",
    builder_poc_name: firstRow.builder_poc_name || "",
    builder_email: firstRow.builder_email || "",
    categories,
    discounts, // Add discounts to return object
  };
}

// Validate parsed project data
export function validateProjectData(data: ParsedProjectData): string[] {
  const errors: string[] = [];

  if (!data.job_location.trim()) {
    errors.push("Job location is required");
  }

  if (data.categories.length === 0) {
    errors.push("At least one category is required");
  }

  for (const category of data.categories) {
    if (!category.name.trim()) {
      errors.push("Category name cannot be empty");
    }

    // Fixed amount categories don't need subcategories
    if (category.isFixedAmount) {
      if (category.fixedAmount < 0) {
        errors.push(`Amount cannot be negative for "${category.name}"`);
      }
    } else {
      // Normal categories need at least one subcategory
      if (category.subcategories.length === 0) {
        errors.push(
          `Category "${category.name}" must have at least one subcategory or be a fixed amount`,
        );
      }
      for (const sub of category.subcategories) {
        if (!sub.name.trim()) {
          errors.push(
            `Subcategory name cannot be empty in category "${category.name}"`,
          );
        }
        if (sub.amount < 0) {
          errors.push(
            `Amount cannot be negative for "${category.name} - ${sub.name}"`,
          );
        }
      }
    }
  }

  return errors;
}
